import React, { useState } from 'react';
import {
  TextField,
  Box,
  FormControl,
  InputLabel,
  FormHelperText,
  Dialog,
  DialogContent,
  InputAdornment,
  Typography,
  IconButton,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

const COUNTRIES: Country[] = [
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
  { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
];

interface PhoneInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  onCountryChange?: (country: Country) => void;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label = 'Phone Number',
  value,
  onChange,
  error,
  helperText,
  disabled = false,
  required = false,
  fullWidth = true,
  onCountryChange
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]); // Default to India
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handlePhoneInput = (text: string) => {
    // Only allow numeric input
    const numericOnly = text.replace(/[^0-9]/g, '');
    onChange(numericOnly);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setShowCountryModal(false);
    setSearchQuery('');
    onCountryChange?.(country);
  };

  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.dialCode.includes(searchQuery)
  );

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <FormControl fullWidth={fullWidth} error={!!error} disabled={disabled}>
        {label && (
          <InputLabel 
            shrink 
            sx={{ 
              position: 'relative',
              transform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
              '&.Mui-error': {
                color: 'text.primary'
              }
            }}
          >
            {label} {required && '*'}
          </InputLabel>
        )}
        
        <Box sx={{ display: 'flex', gap: 1.5, mt: label ? 3 : 0 }}>
          {/* Country Code Selector */}
          <Box
            onClick={() => !disabled && setShowCountryModal(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              minWidth: '90px',
              maxWidth: '120px',
              height: '48px',
              px: 1,
              py: 1.5,
              border: 1,
              borderColor: error ? 'error.main' : 'divider',
              borderRadius: '8px',
              backgroundColor: 'background.paper',
              cursor: disabled ? 'not-allowed' : 'pointer',
              '&:hover': {
                borderColor: disabled ? 'divider' : 'primary.main',
                backgroundColor: disabled ? 'background.paper' : 'action.hover'
              }
            }}
          >
            <Typography sx={{ fontSize: '1.25rem' }}>
              {selectedCountry.flag}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              {selectedCountry.dialCode}
            </Typography>
          </Box>

          {/* Phone Number Input */}
          <TextField
            fullWidth
            placeholder="9876543210"
            value={value}
            onChange={(e) => handlePhoneInput(e.target.value)}
            error={!!error}
            disabled={disabled}
            inputProps={{
              maxLength: 15,
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '48px',
                '& input': {
                  fontSize: '1rem',
                  fontWeight: 400,
                  padding: '12px 16px'
                }
              }
            }}
          />
        </Box>

        {helperText && (
          <FormHelperText error={error} sx={{ mt: 0.5, fontSize: '0.75rem' }}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>

      {/* Country Selection Modal */}
      <Dialog
        open={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh'
          }
        }}
      >
        {/* Modal Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px'
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#000000' }}>
            Select Country
          </Typography>
          <IconButton 
            onClick={() => setShowCountryModal(false)}
            sx={{ 
              padding: '4px',
              color: '#666666'
            }}
          >
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0 }}>
          {/* Search Input */}
          <Box sx={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>

          {/* Country List */}
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredCountries.map((country, index) => (
              <Paper
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  borderBottom: index < filteredCountries.length - 1 ? '1px solid #e5e7eb' : 'none',
                  cursor: 'pointer',
                  backgroundColor: selectedCountry.code === country.code ? '#f0fdf4' : '#ffffff',
                  '&:hover': {
                    backgroundColor: selectedCountry.code === country.code ? '#e6f9f0' : '#f9fafb'
                  }
                }}
              >
                <Typography sx={{ fontSize: '24px' }}>
                  {country.flag}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#000000', lineHeight: '1.5' }}>
                    {country.name}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#666666', fontWeight: 500, marginTop: '4px' }}>
                    {country.dialCode}
                  </Typography>
                </Box>
                {selectedCountry.code === country.code && (
                  <Typography sx={{ color: '#14b8a6', fontSize: '20px', fontWeight: 700 }}>
                    ✓
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
